import { useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { type Key, useEffect, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { RoleCondition } from "@/components/RoleCondition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EmptyState, EmptyStateHeading } from "@/components/ui/empty-state";
import { Input, Label, TextField } from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { useAuth } from "@/features/auth/hooks";
import { mutationResponse } from "@/utils/trpc";
import { useNewTestRunDialog } from "./store";

export function NewTestRunButton() {
  const { show, setShow, preselectedKeys } = useNewTestRunDialog();
  const [checked, setChecked] = useState<number[]>([]);
  const [input, setInput] = useState<string>("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orgId, setOrgId] = useState<number>(user.profile.org.id);

  const { data, isLoading } = trpc.runs.pendingOrders.useQuery({
    orgId,
  });

  const labs = trpc.orgs.primaryLabs.useQuery();

  const create = trpc.runs.create.useMutation();

  useEffect(() => {
    setChecked(preselectedKeys as number[]);
  }, [preselectedKeys]);

  const onAdd = async (testSpecIds: number[], description: string) => {
    create.mutate(
      {
        description,
        testSpecIds,
        orgId,
      },
      mutationResponse({
        errorTitle: "Error creating test run",
        successMessage: "Test run created",
        onSuccess: (data) => {
          setShow(false);
          if (data.isProcedural) {
            navigate({
              to: "/runs/$id/workspace",
              params: {
                id: data.id,
              },
              search: { tab: "overview", day: null },
            });
          } else {
            navigate({ to: "/runs/$id", params: { id: data.id } });
          }
        },
      }),
    );
  };

  const onSelectionChange = (keys: "all" | Set<Key>) => {
    const selected =
      keys === "all"
        ? data?.map((test) => test.id) || []
        : (Array.from(keys) as number[]);

    setChecked(selected);
  };

  return (
    <DialogTrigger isOpen={show} onOpenChange={setShow}>
      <Button size="sm">
        <PlusIcon />
        New test run
      </Button>
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>New test run</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <RoleCondition roles={["blis_lab_manager"]}>
              <Select
                selectedKey={orgId}
                onSelectionChange={(key) => {
                  setOrgId(key as number);
                }}
              >
                <Label>Lab</Label>
                <SelectTrigger />
                <SelectList items={labs.data || []}>
                  {(lab) => (
                    <SelectItem id={lab.id} textValue={lab.name}>
                      {lab.name}
                    </SelectItem>
                  )}
                </SelectList>
              </Select>
            </RoleCondition>
            <TextField value={input} onChange={setInput}>
              <Label>Description</Label>
              <Input placeholder="Enter description" />
            </TextField>
            <div className="flex items-center gap-2 mt-2 mb-0 mx-3">
              <Checkbox
                id="testrun-selectall"
                isDisabled={!data?.length}
                isIndeterminate={
                  checked.length > 0 && checked.length < (data?.length || 0)
                }
                isSelected={checked.length === (data?.length || 0)}
                onChange={(checked) => {
                  if (checked === true) {
                    setChecked(data?.map((test) => test.id) || []);
                  } else if (checked === false) {
                    setChecked([]);
                  }
                }}
              />
              <Label htmlFor="testrun-selectall">Select all</Label>
            </div>
            <GridList
              items={data || []}
              selectionMode="multiple"
              selectedKeys={checked}
              aria-label="Pending orders"
              onSelectionChange={onSelectionChange}
              renderEmptyState={() => (
                <EmptyState>
                  <EmptyStateHeading>No pending orders</EmptyStateHeading>
                </EmptyState>
              )}
            >
              {(test) => (
                <GridListItem key={test.id} id={test.id} textValue={test.name}>
                  <div className="flex items-center justify-between gap-2 w-full">
                    <Text>{test.name}</Text>
                    <Badge>{test.testOrders.length}</Badge>
                  </div>
                </GridListItem>
              )}
            </GridList>
          </DialogBody>
          <DialogFooter>
            <Button
              onPress={() => onAdd(checked, input)}
              isPending={create.isPending}
              isDisabled={checked.length === 0}
            >
              Add
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
